import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { ApiBaseService } from './api/api-base.service';
import ApiOptionsConfig from './api/api-options.config';

const BASE_URL = '/audits-logs';

export interface TestLogEntryAttachment {
  fileName?: string;
  fileNameOriginal?: string;
}

export interface TestLogEntry {
  author: string;
  dateTime: string;
  status: 'PASSED' | 'FAILED' | 'SKIPPED';
  issueDescription?: string;
  projectIssueId?: number;
  attachments: TestLogEntryAttachment[];
}

export interface TestLogModalContext {
  testTitle: string;
  projectTestlistStepId: number;
  logs: TestLogEntry[];
  isLoading: boolean;
}

export interface TestLogModalState {
  isOpen: boolean;
  context: TestLogModalContext | null;
}

@Injectable({
  providedIn: 'root'
})
export class TestLogModalService extends ApiBaseService {
  private readonly stateSubject = new BehaviorSubject<TestLogModalState>({
    isOpen: false,
    context: null
  });

  readonly state$ = this.stateSubject.asObservable();

  open(testTitle: string, projectTestlistStepId: number): void {
    this.stateSubject.next({
      isOpen: true,
      context: { testTitle, projectTestlistStepId, logs: [], isLoading: true }
    });

    this.listTestLogs(projectTestlistStepId).subscribe({
      next: (response: any) => {
        const auditLogs: any[] = response?.data?.auditLogs ?? [];
        const logs: TestLogEntry[] = auditLogs.map(entry => ({
          author: `${entry.creatorUser?.firstName ?? ''} ${entry.creatorUser?.lastName ?? ''}`.trim(),
          dateTime: entry.createTime,
          status: entry.newData?.projectTestlistStep?.status ?? 'SKIPPED',
          issueDescription: entry.newData?.projectTestlistStep?.issueDescription,
          projectIssueId: entry.newData?.projectTestlistStep?.projectIssueId,
          attachments: entry.newData?.projectTestlistStep?.attachments ?? []
        }));
        this.updateContext({ logs, isLoading: false });
      },
      error: () => {
        this.updateContext({ isLoading: false });
      }
    });
  }

  private updateContext(partial: Partial<TestLogModalContext>): void {
    const current = this.stateSubject.getValue();
    if (current.context) {
      this.stateSubject.next({
        ...current,
        context: { ...current.context, ...partial }
      });
    }
  }

  listTestLogs(projectTestlistStepId: number, options?: ApiOptionsConfig): Observable<any> {
    return this.GET(this.getUrl(`${BASE_URL}/PROJECT_TESTLIST_STEP/${projectTestlistStepId}`), options?.params);
  }

  close(): void {
    this.stateSubject.next({ isOpen: false, context: null });
  }
}
